import React, { useEffect, useState } from "react";
import Table from "../Common/Table/Table";
import PageHeader from "../PageHeader";
import { hasAccess } from "../../utils";
import { useDispatch, useSelector } from "react-redux";
import {
  CreateBeneficiary,
  createBeneficiary,
  getBeneficiary,
  GetByBeneficiary,
  getByIdBeneficiary,
} from "../../actions/beneficiary/beneficiary";
import { Button, Form, Input, message, Modal, Typography } from "antd";
import { useFormik } from "formik";
import * as yup from "yup";
import editPNG from "../../assets/icons/edit.png";
import deletePNG from "../../assets/icons/delete.png";
import disclaimerIcon from "../../assets/icons/disclaimer_icon.png";
import usePayAxios from "../../config/useAxios";
import { ClipLoader } from "react-spinners";
import { userCards } from "../../actions/cards/cards";
import moment from "moment";
const { Text, Title } = Typography;
const ValidationSchema = yup.object().shape({
  beneficiary_name: yup
    .string()
    .matches(
      /^[A-Za-z\s]+$/,
      "Beneficiary name can only contain letters and spaces",
    )
    .required("Beneficiary name is required")
    .min(1, "Beneficiary name must be at least 1 character")
    .max(50, "Beneficiary name should not exceed more than 50 characters"),

  nick_name: yup
    .string()
    .matches(/^[A-Za-z\s]+$/, "Nick name can only contain letters and spaces")
    .max(8, "Nick name should not exceed more than 8 characters"),

  // --- Updated Account Number Validation ---
  account_number: yup
    .string()
    .matches(/^[0-9]+$/, "Account number can only contain digits") // Enforce Numeric
    .required("Account number is required")
    .min(1, "Account number must be at least 1 digit")
    .max(16, "Account number cannot exceed 16 digits"),

  // --- Updated IFSC Code Validation ---
  ifsc_code: yup
    .string()
    .matches(/^[A-Za-z0-9]+$/, "IFSC code can only contain letters and digits") // Enforce Alphanumeric
    .required("IFSC code is required")
    .min(1, "IFSC code must be at least 1 character")
    .max(15, "IFSC code cannot exceed 15 characters"),

  // --- Updated Bank Name Validation ---
  bank_name: yup
    .string()
    .matches(/^[A-Za-z\s]+$/, "Bank name can only contain letters and spaces") // Enforce Alphabetic
    .min(1, "Bank name must be at least 1 character")
    .max(20, "Bank name cannot exceed 20 characters")
    .required("Bank name is required"),

  primary_contact: yup
    .string()
    .matches(/^\d{10}$/, "Mobile number must be exactly 10 digits")
    .required("Mobile number is required"),

  email: yup.string().email("Enter a valid email address."),

  // address: yup.string().required(), // Keeping this commented out as it's not required in your list.
});

const maskEmail = (email) => {
  if (!email || !email.includes("@")) return email; // Return as is if invalid email

  const [firstPart, secondPart] = email.split("@");

  // If first part is too short to mask, return the original email
  if (firstPart.length <= 2) {
    return email;
  }

  // Masking logic for emails with a length greater than 2
  return `${firstPart[0]}${"*".repeat(firstPart.length - 2)}${firstPart.slice(
    -1,
  )}@${secondPart}`;
};

// Function to mask the mobile number
const maskMobile = (mobile) => {
  if (!mobile || mobile.length < 4) return mobile;
  return `******${mobile.slice(-4)}`;
};

// Function to mask the card number
const maskAccountNumber = (accountNumber) => {
  if (!accountNumber) return accountNumber;
  return `XXXX XXXX XXXX ${accountNumber.slice(-4)}`;
};

const Beneficiary = () => {
  const dispatch = useDispatch();
  const {
    onCall: onCallDeleteBeneficiary,
    data: deleteBeneficiaryData,
    reset: deleteBeneficiaryReset,
    loading: deleteBeneficiaryLoading,
  } = usePayAxios({ api: "api/admin/beneficiaries/v1/delete", method: "post" });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [rowData, setRowData] = useState({});
  const [page, setPage] = React.useState(1);
  const [isDelete, setIsDelete] = React.useState(false);
  const [filters, setFilters] = React.useState({});
  const [isBeneficiaryOpen, setIsBeneficiaryOpen] = React.useState(false);
  const beneficiaryData = useSelector(
    (state) => state?.beneficiary?.beneficiaryData?.data?.response,
  );
  const beneficiaryTotalRecords = useSelector(
    (state) => state?.beneficiary?.beneficiaryData?.data,
  );
  const beneficiaryCreateData = useSelector(
    (state) => state?.beneficiary?.createBeneficiaryData?.data,
  );
  const beneficiaryDataByID = useSelector(
    (state) =>
      state?.beneficiary?.beneficiaryDataByID?.data?.response?.beneficiary,
  );
  const userCardsResponse = useSelector(
    (state) => state.cards.userCards?.data?.response?.cards || {},
  );
  console.log("row data in user", rowData?.id);
  // const beneficiaryDataByID = useSelector((state) => state?.beneficiary?.beneficiaryDataByID?.data?.response?.beneficiary);
  const formik = useFormik({
    initialValues: {
      ifsc_code: "",
      beneficiary_name: "",
      nick_name: "",
      account_number: "",
      bank_name: "",
      email: "",
      address: "",
      primary_contact: "",
    },
    onSubmit: (vals) => {
      // Destructure and initialize bank_account_details directly
      const { account_number, bank_name, ifsc_code, ...rest } = vals;
      let bank_account_details = { account_number, bank_name, ifsc_code };

      // Update vals to exclude the removed properties
      vals = rest;
      if (rowData?.id) {
        vals.id = rowData?.id;
      }

      dispatch(
        createBeneficiary({
          ...vals,
          bank_account_details,
          card_id: userCardsResponse?.[0]?.id,
        }),
      );
    },
    validationSchema: ValidationSchema,
  });
  const columns = [
    {
      title: "Beneficiary Name",
      dataIndex: "beneficiary_name",
      key: "beneficiary_name",
      render: (text, record) => {
        return (
          <div
            className={`cursor render_common_style status_${record?.status}`}
          >
            {text}
          </div>
        );
      },
    },
    {
      title: "Nick Name",
      dataIndex: "nick_name",
      key: "nick_name",
    },
    {
      title: "Account No",
      dataIndex: "bank_account_details",
      key: "bank_account_details",
      render: (text) =>
        text ? <div>{maskAccountNumber(text?.account_number)}</div> : "",
    },
    {
      title: "Bank Name",
      dataIndex: "bank_account_details",
      key: "bank_account_details",
      render: (text) => <div>{text?.bank_name}</div>,
    },
    {
      title: "Mobile Number",
      dataIndex: "primary_contact",
      key: "primary_contact",
      render: (text) => (text ? <div>{maskMobile(text)}</div> : ""),
    },
    {
      title: "Email Address",
      dataIndex: "email",
      key: "email",
      render: (text) => (text ? <div>{maskEmail(text)}</div> : ""),
    },
    {
      title: "Actions",
      dataIndex: "",
      key: "",
      render: (_, record) => (
        <div className="d-flex">
          {/* <div onClick={() => {
                        setIsModalVisible(true);
                        setRowData(record);
                    }}>
                        <img src={editPNG} alt="edit icon" width={25} />
                    </div> */}
          <div
            style={{ marginLeft: "0.5rem" }}
            onClick={(e) => {
              e.stopPropagation();
              setIsDelete(true);
              setRowData(record);
            }}
          >
            <img src={deletePNG} alt="delete icon" width={25} />
          </div>
        </div>
      ),
    },
  ];
  console.log("beneficiart card is", userCardsResponse);
  useEffect(() => {
    dispatch(getBeneficiary({}));
    dispatch(userCards({}));
  }, []);

  useEffect(() => {
    if (beneficiaryCreateData?.status) {
      setIsModalVisible(false);
      dispatch({ type: CreateBeneficiary?.RESET });
      setRowData({});
      dispatch(getBeneficiary({}));
      formik.resetForm({});
    } else if (beneficiaryCreateData?.status == false) {
      dispatch({ type: CreateBeneficiary?.RESET });
    }
  }, [beneficiaryCreateData]);

  useEffect(() => {
    if (deleteBeneficiaryData?.status == true) {
      dispatch(getBeneficiary({}));
      deleteBeneficiaryReset();
      setRowData({});
      setIsDelete(false);
    }
  }, [deleteBeneficiaryData]);

  // useEffect(() => {
  //   if (rowData?.id) {
  //     // setIsModalVisible(true);
  //     formik.setFieldValue(
  //       "ifsc_code",
  //       rowData?.bank_account_details?.ifsc_code
  //     );
  //     formik.setFieldValue("beneficiary_name", rowData?.beneficiary_name);
  //     formik.setFieldValue(
  //       "account_number",
  //       rowData?.bank_account_details?.account_number
  //     );
  //     formik.setFieldValue(
  //       "bank_name",
  //       rowData?.bank_account_details?.bank_name
  //     );
  //     formik.setFieldValue("email", rowData?.email);
  //     formik.setFieldValue("address", rowData?.address);
  //     formik.setFieldValue("primary_contact", rowData?.primary_contact);
  //   }
  // }, [rowData, isModalVisible]);

  const handleCancel = () => {
    setIsModalVisible(false);
    setIsBeneficiaryOpen(false);
    setRowData({});
    dispatch({ type: GetByBeneficiary?.RESET });
    formik.resetForm({});
  };
  const handleAddButton = () => {
    setRowData({});
    setIsModalVisible(true);
  };
  const handleRowClick = (_, record) => {
    setRowData(record);
    setIsBeneficiaryOpen(true);
  };

  const handleSearch = (value) => {
    dispatch(
      getBeneficiary({ ...filters, page: 1, search: value, source: "admin" }),
    );
    setFilters({
      ...filters,
      search: value,
    });
    setPage(1);
  };

  const Disclaimer = () => (
    <div className="disclaimer-element mb1">
      <div className="disclaimer-head">
        <img src={disclaimerIcon} alt="disclaimerIcon" />
        <h1>Disclaimer</h1>
      </div>
      <p>
        Sending Money for an Investment, Job Offer, Crypto Currency, Forex
        Trading etc.? Please verify if the beneficiary is genuine before
        proceeding.
      </p>
      {/* <p>
        2. As per RBI guidelines, credit will be effected based solely on the
        Beneficiary Account Number information and not on the Beneficiary Name
        particulars. Hence, check the entered account number twice.
      </p> */}
      {/* <p>3. The maximum transfer limit per month per registered beneficiary is Rs. 1 Lakh.</p> */}
    </div>
  );

  const detailsData = [
    {
      label: "Beneficiary Name",
      value: rowData?.beneficiary_name || "-",
    },
    {
      label: "Nick Name",
      value: rowData?.nick_name || "-",
    },
    {
      label: "Account No",
      value:
        maskAccountNumber(rowData?.bank_account_details?.account_number) || "-",
    },
    {
      label: "Bank Name",
      value: rowData?.bank_account_details?.bank_name || "-",
    },
    {
      label: "IFSC Code",
      value: rowData?.bank_account_details?.ifsc_code || "-",
    },
    {
      label: "Mobile Number",
      value: maskMobile(rowData?.primary_contact) || "-",
    },
    {
      label: "Email Address",
      value: maskEmail(rowData?.email) || "-",
    },
    {
      label: "Address",
      value: rowData?.address || "-",
    },
    {
      label: "Created At",
      value: moment(rowData?.created_at).format("DD/MM/YYYY") || "-",
    },
  ];

  return (
    <>
      {isBeneficiaryOpen && (
        <Modal
          title={<Title level={4}>Beneficiary Details</Title>}
          open={isBeneficiaryOpen}
          onCancel={handleCancel}
          footer={[]}
          className="right-alinged-modal"
        >
          <div className="item-details p1 mt2">
            {detailsData?.map((listElement, listIndex) => (
              <div className="item-detail" key={listIndex}>
                <label>{listElement?.label}</label>
                <p title={listElement?.value}>
                  {listElement?.value ? listElement?.value : "-"}
                </p>
              </div>
            ))}
          </div>
        </Modal>
      )}
      {isModalVisible && (
        <Modal
          title={
            <Title level={4}>
              {rowData?.id ? "Update Beneficiary" : "Create Beneficiary"}
            </Title>
          }
          open={isModalVisible}
          onCancel={handleCancel}
          footer={[]}
          className="right-alinged-modal"
        >
          {rowData?.id === undefined && <Disclaimer />}
          <Form onFinish={formik.handleSubmit} layout="vertical">
            <Form.Item
              label="Beneficiary Name"
              help={formik?.submitCount > 0 && formik.errors.beneficiary_name}
              validateStatus={
                formik?.submitCount > 0 &&
                formik.errors.beneficiary_name &&
                formik.touched.beneficiary_name
                  ? "error"
                  : "success"
              }
              required
            >
              <Input
                placeholder="Enter name"
                name="beneficiary_name"
                value={formik.values.beneficiary_name}
                onChange={formik.handleChange}
              />
            </Form.Item>
            <Form.Item
              label="Nick Name"
              help={formik?.submitCount > 0 && formik.errors.nick_name}
              validateStatus={
                formik?.submitCount > 0 &&
                formik.errors.nick_name &&
                formik.touched.nick_name
                  ? "error"
                  : "success"
              }
            >
              <Input
                placeholder="Enter nick name (optional)"
                name="nick_name"
                value={formik.values.nick_name}
                onChange={formik.handleChange}
              />
            </Form.Item>
            <Form.Item
              label="Account No"
              help={formik?.submitCount > 0 && formik.errors.account_number}
              validateStatus={
                formik?.submitCount > 0 &&
                formik.errors.account_number &&
                formik.touched.account_number
                  ? "error"
                  : "success"
              }
              required
            >
              <Input
                placeholder="Enter account number"
                name="account_number"
                value={formik.values.account_number}
                onChange={(event) => {
                  const { name, value } = event.target;
                  if (new RegExp(/^[0-9]*$/).test(value)) {
                    formik.setFieldValue(name, value);
                  } else if (value === "") {
                    formik.setFieldValue(name, value);
                  }
                }}
                onPaste={(event) => {
                  event.preventDefault();
                  formik.setFieldValue(
                    "account_number",
                    formik.values.account_number,
                  );
                }}
                minLength={9}
                maxLength={16}
              />
            </Form.Item>
            <Form.Item
              label="IFSC Code"
              help={formik?.submitCount > 0 && formik.errors.ifsc_code}
              validateStatus={
                formik?.submitCount > 0 &&
                formik.errors.ifsc_code &&
                formik.touched.ifsc_code
                  ? "error"
                  : "success"
              }
              required
            >
              <Input
                placeholder="Enter ifsc"
                name="ifsc_code"
                value={formik.values.ifsc_code}
                onChange={formik.handleChange}
                onPaste={(event) => {
                  event.preventDefault();
                  formik.setFieldValue("ifsc_code", formik.values.ifsc_code);
                }}
              />
            </Form.Item>
            <Form.Item
              label="Bank Name"
              help={formik?.submitCount > 0 && formik.errors.bank_name}
              validateStatus={
                formik?.submitCount > 0 &&
                formik.errors.bank_name &&
                formik.touched.bank_name
                  ? "error"
                  : "success"
              }
              required
            >
              <Input
                placeholder="Enter bank name"
                name="bank_name"
                value={formik.values.bank_name}
                onChange={formik.handleChange}
              />
            </Form.Item>
            <Form.Item
              label="Mobile Number"
              help={formik?.submitCount > 0 && formik.errors.primary_contact}
              validateStatus={
                formik?.submitCount > 0 &&
                formik.errors.primary_contact &&
                formik.touched.primary_contact
                  ? "error"
                  : "success"
              }
              required
            >
              <Input
                placeholder="Enter mobile"
                name="primary_contact"
                value={formik.values.primary_contact}
                onChange={(event) => {
                  const { name, value } = event.target;
                  if (new RegExp(/^[0-9]*$/).test(value)) {
                    formik.setFieldValue(name, value);
                  } else if (value === "") {
                    formik.setFieldValue(name, value);
                  }
                }}
                maxLength={10}
              />
            </Form.Item>
            <Form.Item
              label="Email Address"
              help={formik?.submitCount > 0 && formik.errors.email}
              validateStatus={
                formik?.submitCount > 0 &&
                formik.errors.email &&
                formik.touched.email
                  ? "error"
                  : "success"
              }
            >
              <Input
                placeholder="Enter email"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
              />
            </Form.Item>
            <Form.Item
              label="Address"
              help={formik?.submitCount > 0 && formik.errors.address}
              validateStatus={
                formik?.submitCount > 0 &&
                formik.errors.address &&
                formik.touched.address
                  ? "error"
                  : "success"
              }
            >
              <Input
                placeholder="Enter address"
                name="address"
                value={formik.values.address}
                onChange={formik.handleChange}
              />
            </Form.Item>
            <div className="note">
              Note: As per RBI guidelines, credit will be effected based solely
              on the Beneficiary Account Number information and not on the
              Beneficiary Name particulars. Hence, check the entered account
              number twice.
            </div>
            <div>
              <Button
                key="1"
                htmlType="submit"
                className="formButton mt3 primaryButton"
              >
                {rowData?.id ? "Update" : "Submit"}
              </Button>
              <Button
                key="2"
                className="cancelButton ml1 mt3 "
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </div>
          </Form>
        </Modal>
      )}
      {isDelete && (
        <Modal
          open={isDelete}
          title="Delete Beneficiary"
          footer={[]}
          onCancel={() => {
            setIsDelete(false);
            setRowData({});
          }}
        >
          Are you sure you want to delete this beneficiary?
          <div className="d-flex">
            <Button
              key="1"
              htmlType="submit"
              className="formButton mt1"
              onClick={() => {
                onCallDeleteBeneficiary({
                  params: {
                    id: rowData?.id,
                  },
                });
              }}
            >
              {deleteBeneficiaryLoading ? (
                <ClipLoader
                  loading={deleteBeneficiaryLoading}
                  size={20}
                  color="#FFF"
                />
              ) : (
                "Confirm"
              )}
            </Button>
            <Button
              key="2"
              className="cancelButton ml1 mt1"
              onClick={() => {
                setIsDelete(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </Modal>
      )}
      <div className="heading-div">
        <PageHeader
          title={`Beneficiary`}
          navigation={{
            Home:
              hasAccess("beneficiary", "read") ||
              hasAccess("beneficiary", "write")
                ? "/dashboard"
                : "",
            Beneficiary: "/beneficiary/",
          }}
        />
      </div>

      <Table
        rowKey="id"
        columns={columns}
        data={beneficiaryData?.beneficiaries}
        btnsOnly={false}
        btnsComponent={
          <>
            <Button
              key="1"
              className="formButton primaryButton"
              onClick={handleAddButton}
            >
              Add Beneficiary
            </Button>
          </>
        }
        pagination={{
          total: beneficiaryTotalRecords?.total_records || 0,
          current: page,

          onChange: (pagee) => {
            dispatch(getBeneficiary({ page: pagee }));
            setPage(pagee);
          },
        }}
        isTopFilter={true}
        filter={true}
        filterMenu={false}
        topFilters={[]}
        isMore={true}
        handleSearch={handleSearch}
        placeholder={"Search..."}
        isActionAfterSearch={true}
        handleRowClick={handleRowClick}
      />
    </>
  );
};

export default Beneficiary;
